/**
 * index.js — Application Entry Point
 *
 * Provider hierarchy (outermost → innermost):
 *   BrowserRouter     — enables URL-based navigation (React Router v6)
 *   LanguageProvider  — i18n / RTL support
 *   AuthProvider      — authentication state + session persistence
 *   SettingsProvider  — backend user settings (needs auth token)
 *   ThemeProvider     — colour theme, backed by the SystemColor setting
 *   BlockingProvider  — opt-in global loading counter for plain-fetch calls
 *   App               — layout shell, data store, RBAC, page rendering
 *
 * BrowserRouter must be outermost so that useNavigate() and useLocation()
 * work in App.js and any component inside the tree.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { LanguageProvider } from './i18n/LanguageContext';
import { ThemeProvider }    from './context/ThemeContext';
import { AuthProvider }     from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { BlockingProvider } from './hooks/useBlockingTracker';

const queryClient = new QueryClient();

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <SettingsProvider>
              <ThemeProvider>
                <BlockingProvider>
                  <App />
                </BlockingProvider>
              </ThemeProvider>
            </SettingsProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
